import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth, db } from "../../backend/firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import Dataset from "../../components/Dataset";
import "./DownloadScreen.css";

const DownloadScreen = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [downloads, setDownloads] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadDownloads = async () => {
      try {
        if (!uid) {
          setError("No user ID provided");
          setLoading(false);
          return;
        }

        // Fetch all downloads under user's collection
        const downloadsRef = collection(db, "Users", uid, "downloads");
        const downloadsQuery = query(
          downloadsRef,
          orderBy("downloadedAt", "desc")
        );
        const snap = await getDocs(downloadsQuery);

        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setDownloads(list);

        // Extract unique dataset IDs
        const uniqueDatasetIds = [
          ...new Set(list.map((d) => d.datasetId).filter(Boolean)),
        ];

        // Create dataset objects for each unique dataset
        const datasetObjects = uniqueDatasetIds.map((datasetId) => {
          const datasetDownloads = list.filter(
            (d) => d.datasetId === datasetId
          );
          return {
            id: datasetId,
            downloads: datasetDownloads,
            totalDownloads: datasetDownloads.length,
            latestDownload: datasetDownloads[0]?.downloadedAt,
          };
        });

        setDatasets(datasetObjects);
      } catch (err) {
        console.error("Error fetching downloads:", err);
        setError("Failed to load download history. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadDownloads();
  }, [uid]);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  };

  const getFileSize = (fileId) => {
    // Mock function - implement actual file size retrieval
    const sizes = ["1.2 MB", "850 KB", "2.3 MB", "450 KB", "3.1 MB"];
    return sizes[Math.floor(Math.random() * sizes.length)];
  };

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    window.location.reload();
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="download-screen">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading download history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="download-screen">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Error Loading Downloads</h2>
          <p className="error-message">{error}</p>
          <button onClick={handleRetry} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="download-screen">
      {/* Datasets Section */}
      <div className="datasets-section">
        <div className="section-header">
          <h2>Your Downloaded Datasets</h2>
        </div>

        {datasets.length === 0 ? (
          <div className="empty-datasets">
            <div className="empty-icon">📁</div>
            <p>No datasets downloaded yet</p>
            <p className="empty-subtext">Download datasets to see them here</p>
          </div>
        ) : (
          <div className="datasets-list">
            {datasets.map((dataset) => (
              <Dataset datasetid={dataset.id} downloads={dataset.downloads} />
            ))}
          </div>
        )}
      </div>

      {/* All Downloads Table */}
      <div className="downloads-section">
        <div className="section-header">
          <h2>All Downloads</h2>
          <span className="downloads-count">
            {downloads.length} download{downloads.length !== 1 ? "s" : ""}
          </span>
        </div>

        {downloads.length === 0 ? (
          <div className="empty-downloads">
            <div className="empty-icon">📥</div>
            <p>No downloads found</p>
            <p className="empty-subtext">
              Your download history will appear here
            </p>
          </div>
        ) : (
          <div className="downloads-container">
            <table className="downloads-table">
              <thead>
                <tr>
                  <th>Dataset</th>
                  <th>Version</th>
                  <th>Downloaded On</th>
                </tr>
              </thead>
              <tbody>
                {downloads.map((download) => (
                  <tr key={download.id} className="download-row">
                    <td>
                      <div className="dataset-cell">
                        <span className="dataset-name">
                          {download.datasetId || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="version-tag">
                        {download.version || "Latest"}
                      </span>
                    </td>
                    <td>
                      <div className="time-cell">
                        <span className="timestamp">
                          {formatDate(download.downloadedAt)}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadScreen;
